'use client'

import { motion } from 'framer-motion'
import { PanelLeftOpenIcon, PanelRightOpenIcon } from 'lucide-react'
import type { ComponentType, ReactNode, RefObject } from 'react'
import { Fragment, useId, useRef } from 'react'

import { OrderXLogoIcon } from '@/components/icons'
import { ShellLogoIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { onActivateKey } from '@/lib/utils/activate-key'

/** The active item's fill glides to the newly selected item rather than jumping. */
export const RAIL_ACTIVE_SPRING = { type: 'spring', stiffness: 520, damping: 42 } as const

/** A collapsed rail icon's name, shown beside it on hover (prototype .tip-right). */
export function RailTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="px-2 py-1 text-[11px] leading-4">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

export interface AppSidePanelItem {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
  active?: boolean
  disabled?: boolean
  /** Extra space above this item — mirror any grouping gap the expanded layout has so the
   *  icons sit at the same height in both states. */
  gapBefore?: boolean
  /** Shows a dot on the icon — e.g. a research report waiting to be read. The text is its label. */
  indicator?: string
  onSelect: () => void
  /** Pointer or keyboard focus landed on the item — a click is likely, e.g. warm its route. */
  onIntent?: () => void
}

interface AppSidePanelProps {
  ariaLabel: string
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  items: AppSidePanelItem[]
  children?: ReactNode
  expandedContent?: ReactNode
  footer?: ReactNode
  className?: string
  containerRef?: RefObject<HTMLElement | null>
  showLogo?: boolean
  showActiveItemWhenCollapsed?: boolean
  /** Renders a back button in the expanded logo row (next to the collapse toggle) — e.g.
   *  "return to the page that opened this panel". Collapsed state has no room for it here;
   *  callers needing back-navigation while collapsed too should also surface it in `footer`. */
  backAction?: { label: string; onClick: () => void }
  /** Hover tooltips on the collapsed rail's icons and logo/collapse toggles. */
  tooltips?: boolean
}

export function AppSidePanel({
  ariaLabel,
  collapsed,
  onCollapsedChange,
  items,
  children,
  expandedContent,
  footer,
  className,
  containerRef,
  showLogo = false,
  showActiveItemWhenCollapsed = true,
  backAction,
  tooltips = true,
}: AppSidePanelProps) {
  const asideRef = useRef<HTMLElement | null>(null)
  // Shared-layout id for the sliding active fill; unique per panel (Trade, Settings).
  const activeFillId = `rail-active-${useId()}`
  // The rail no longer opens on hover — its icons name themselves in a tooltip instead. It
  // opens only when asked: the logo button or the keyboard (and pages that open it for their
  // own navigation, like the assistant's chat history).
  const expandExplicitly = () => onCollapsedChange(false)

  const itemButtons = items.map((item) => {
    const showActive = item.active && (!collapsed || showActiveItemWhenCollapsed)
    // Always full-width, square, with a fixed left inset, instead of swapping between
    // w-9/justify-center (collapsed) and w-full/px-3 (expanded) classes the instant
    // `collapsed` flips. The label uses the
    // "grid-template-columns: 0fr -> 1fr" reveal below instead of being conditionally
    // mounted, so nothing here fights the <aside>'s own animated width with an instant
    // layout snap — that mismatch (children jumping to final layout while the parent was
    // still 40px into a 176px-wide transition) was what made the open/close feel stiff.
    const button = (
      <button
        type="button"
        // What the welcome tour points at.
        data-nav-item={item.id}
        disabled={item.disabled}
        aria-current={
          item.active && (!collapsed || showActiveItemWhenCollapsed) ? 'page' : undefined
        }
        aria-label={collapsed ? item.label : undefined}
        onClick={item.onSelect}
        onPointerEnter={item.onIntent}
        onFocus={item.onIntent}
        className={cn(
          // Figma "Rail item": 36px square, radius sm. The icon sits 10px in, so the collapsed
          // rail's 36px column centres it and the expanded row keeps it in the same spot.
          'relative flex h-9 w-full cursor-pointer items-center rounded-sm pl-2.5 text-sm font-medium tracking-[-0.02em] transition-[color,background-color,scale] duration-150 disabled:cursor-not-allowed disabled:opacity-40 motion-safe:active:scale-[0.94]',
          item.gapBefore && 'mt-2',
          // Same active/idle styles as the trade panel's expanded rows, so nothing changes
          // colour when the rail swaps to the expanded layout.
          showActive ? 'text-emphasis-foreground' : 'text-orderx-button-mint hover:bg-foreground/5'
        )}
      >
        {showActive && (
          <motion.span
            layoutId={activeFillId}
            transition={RAIL_ACTIVE_SPRING}
            aria-hidden="true"
            className="bg-emphasis absolute inset-0 rounded-sm"
          />
        )}
        <span className="relative shrink-0">
          <item.icon className="size-4" />
          {item.indicator && (
            <span
              className="bg-ox-cyan absolute -top-0.5 -right-0.5 size-1.5 rounded-full"
              aria-label={item.indicator}
            />
          )}
        </span>
        <span
          className="relative grid min-w-0 transition-[grid-template-columns] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"
          style={{ gridTemplateColumns: collapsed ? '0fr' : '1fr' }}
        >
          <span className="min-w-0 overflow-hidden">
            <span className="block truncate py-0 pr-3 pl-2 text-left">{item.label}</span>
          </span>
        </span>
      </button>
    )

    // Collapsed icons carry no visible label — a tooltip is the only way to learn what one
    // does. Expanded rows already spell the label out, so skip the tooltip machinery there.
    if (!collapsed || !tooltips) return <Fragment key={item.id}>{button}</Fragment>

    return (
      <RailTooltip key={item.id} label={item.label}>
        {button}
      </RailTooltip>
    )
  })

  return (
    <aside
      ref={(node) => {
        asideRef.current = node
        if (containerRef) containerRef.current = node
      }}
      aria-label={ariaLabel}
      aria-expanded={!collapsed}
      role={collapsed ? 'button' : undefined}
      tabIndex={collapsed ? 0 : undefined}
      data-collapsed={collapsed}
      onKeyDown={collapsed ? onActivateKey(expandExplicitly) : undefined}
      className={cn(
        'text-orderx-button-mint relative flex h-full shrink-0 flex-col overflow-hidden transition-[width,top] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
        collapsed ? 'w-14 cursor-default' : 'w-56',
        className
      )}
    >
      {showLogo || !collapsed ? (
        // The logo row lives outside the padded column below, at a fixed w-56 pinned to the
        // aside's left edge, so nothing in it moves when the panel toggles: both logos stay
        // put and cross-fade, and the collapse toggle sits at its final spot to be revealed
        // by the aside's widening clip (a justify-end row would pop it in over the logo
        // mark and slide it right with the growing width).
        <div className="absolute top-2.5 left-0 z-10 h-6 w-56">
          {showLogo ? (
            <>
              {/* The logo opens the panel; it shows the expand affordance only on
                  focus-visible, so the mark stays the mark under the pointer. */}
              <Tooltip open={tooltips ? undefined : false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    inert={!collapsed}
                    aria-hidden={!collapsed}
                    onClick={expandExplicitly}
                    aria-label="Open sidebar"
                    className={cn(
                      // The resize cursor says "this widens the panel", as sidebar toggles do.
                      'group text-orderx-button-mint absolute top-0 left-4 size-6 cursor-e-resize p-0 transition-opacity duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
                      collapsed ? 'opacity-100' : 'pointer-events-none opacity-0'
                    )}
                  >
                    <ShellLogoIcon
                      aria-hidden="true"
                      className="absolute transition-opacity group-focus-visible:opacity-0"
                    />
                    <PanelLeftOpenIcon
                      aria-hidden="true"
                      className="absolute size-4 opacity-0 transition-opacity group-focus-visible:opacity-100"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  sideOffset={8}
                  className="px-2 py-1 text-[11px] leading-4"
                >
                  Open sidebar
                </TooltipContent>
              </Tooltip>
              {/* Full wordmark while expanded, its X mark in the same teal as the collapsed
                  icon. Its X is the same outline as OrderXSimpleLogoIcon at 23/28 of the
                  height, so h-6 renders it at ~20px, matching the collapsed icon's size-5. */}
              <OrderXLogoIcon
                aria-hidden="true"
                className={cn(
                  'text-orderx-button-mint pointer-events-none absolute top-0.5 left-4 h-5 w-auto transition-opacity duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] [--logo-mark-fill:#86CECE] motion-reduce:transition-none',
                  collapsed ? 'opacity-0' : 'opacity-100'
                )}
              />
            </>
          ) : null}
          {!collapsed ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCollapsedChange(true)}
              aria-label="Collapse side panel"
              title={tooltips ? 'Collapse side panel' : undefined}
              className="text-orderx-button-mint absolute top-0 right-3 size-6"
            >
              <PanelRightOpenIcon className="size-4 shrink-0" />
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={cn('flex h-full min-h-0 flex-col pt-4', collapsed ? 'px-2.5' : '')}>
        {/* Holds the logo row's space in the column. */}
        {showLogo || !collapsed ? <div aria-hidden="true" className="h-6 shrink-0" /> : null}

        {expandedContent ? (
          <div className="relative mt-3 min-h-0 flex-1">
            {/* The two layers swap without sliding: both put every icon at the same spot in
                the same colour, so opening is an instant swap (the labels are then revealed by
                the aside's widening clip) and closing only fades the expanded layer out over
                the already-visible rail. A cross-fade with a slide made icons dim and nudge. */}
            <nav
              inert={!collapsed}
              aria-hidden={!collapsed}
              className={cn(
                'absolute inset-x-0 top-0 flex flex-col gap-1.5',
                collapsed ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
              aria-label={ariaLabel}
            >
              {itemButtons}
            </nav>
            <div
              inert={collapsed}
              aria-hidden={collapsed}
              className={cn(
                // Fixed at the expanded width (and pinned to the aside's left edge in both
                // states — the collapsed rail's px-1.5 would otherwise shift it) so its rows
                // never re-wrap or re-truncate while the aside's width animates.
                'absolute top-0 bottom-0 w-56',
                collapsed
                  ? 'pointer-events-none -left-2.5 opacity-0 transition-opacity duration-150 ease-out motion-reduce:transition-none'
                  : 'left-0 opacity-100'
              )}
            >
              {expandedContent}
            </div>
          </div>
        ) : (
          <>
            <nav
              className={cn(
                'flex shrink-0 flex-col gap-1.5',
                collapsed ? (showLogo ? 'mt-3' : 'mt-0') : 'mt-6'
              )}
              aria-label={ariaLabel}
            >
              {itemButtons}
            </nav>
            {!collapsed && children ? <div className="mt-6 min-h-0 flex-1">{children}</div> : null}
          </>
        )}
        <div className="mt-auto shrink-0 pt-1.5 pb-3.5">{footer}</div>
      </div>
    </aside>
  )
}
